
import type React from "react"
import { useRef, useEffect } from "react"
import { useLocation, useNavigate } from "react-router"
import { AppSidebar } from "./app-sidebar"
import { MobileBottomNav } from "./mobile-nav"
import { useAuth } from "@/lib/auth/auth-context"

export function AppLayout({ children }: { children: React.ReactNode }) {
  const mainRef = useRef<HTMLElement>(null)
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { user, isImpersonating, endImpersonation } = useAuth()

  useEffect(() => {
    mainRef.current?.scrollTo(0, 0)
  }, [pathname])

  return (
    <div className="flex h-screen overflow-hidden bg-zinc-50">
      {/* Sidebar spacer: desktop only (72px to match collapsed sidebar width) */}
      <div className="hidden md:block w-[72px] shrink-0" />

      {/* Sidebar: desktop only, fixed position */}
      <div className="hidden md:block">
        <AppSidebar />
      </div>

      <div className="flex flex-1 flex-col overflow-hidden min-w-0">
        {/* Impersonation banner */}
        {isImpersonating && user && (
          <div className="bg-amber-400 text-amber-950 text-sm font-medium px-4 py-2 flex items-center justify-between shrink-0">
            <span>Impersonating <strong>{user.email}</strong>{user.admin ? " (Admin)" : ""}</span>
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  endImpersonation()
                  navigate('/admin/dashboard')
                }}
                className="bg-amber-950 text-amber-100 px-3 py-1 rounded-lg text-xs font-semibold hover:bg-amber-900 transition-colors"
              >
                End Impersonation
              </button>
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="underline hover:no-underline text-xs"
              >
                Back to Admin
              </button>
            </div>
          </div>
        )}

        {/* Mobile bottom nav is 72px tall + safe-area; pad the main scroll
            so the last row of content isn't hidden behind it on iOS notches.
            Desktop has no bottom nav, so md+ resets to 0. */}
        <main
          ref={mainRef}
          className="flex-1 overflow-y-auto pb-[calc(env(safe-area-inset-bottom)+88px)] md:pb-0"
        >
          {children}
        </main>
      </div>

      {/* Bottom nav: mobile only */}
      <div className="md:hidden">
        <MobileBottomNav />
      </div>
    </div>
  )
}
